import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useShareStore } from '@/stores/shareStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import type { SharePermissions, ShareProfile } from '@/types/sharing';
import { PERMISSION_LABELS, DEFAULT_PERMISSIONS } from '@/types/sharing';

const ICONS = ['📋', '📊', '🏦', '🔧', '🏠', '🤝', '📁', '🔍', '💼', '📝'];

export function ShareProfileManager() {
  const { profiles, fetchProfiles, createProfile, updateProfile, deleteProfile } = useShareStore();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const systemProfiles = profiles.filter((p) => p.user_id === null);
  const userProfiles = profiles.filter((p) => p.user_id !== null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Freigabe-Profile</h2>
          <p className="text-sm text-muted-foreground">
            Wiederverwendbare Vorlagen für deine Freigabe-Links
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          Profil erstellen
        </Button>
      </div>

      {/* System-Profile */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          System-Vorlagen
        </p>
        <div className="space-y-2">
          {systemProfiles.map((profile) => (
            <ProfileCard
              key={profile.id}
              profile={profile}
              readonly
            />
          ))}
        </div>
      </div>

      {/* Eigene Profile */}
      {(userProfiles.length > 0 || showCreate) && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            Meine Profile
          </p>
          <div className="space-y-2">
            {showCreate && (
              <ProfileForm
                onSave={async (data) => {
                  await createProfile(data);
                  setShowCreate(false);
                  toast.success('Profil erstellt');
                }}
                onCancel={() => setShowCreate(false)}
              />
            )}
            {userProfiles.map((profile) =>
              editingId === profile.id ? (
                <ProfileForm
                  key={profile.id}
                  initialData={profile}
                  onSave={async (data) => {
                    await updateProfile(profile.id, data);
                    setEditingId(null);
                    toast.success('Profil gespeichert');
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  onEdit={() => setEditingId(profile.id)}
                  onDelete={async () => {
                    await deleteProfile(profile.id);
                    toast.success('Profil gelöscht');
                  }}
                />
              )
            )}
          </div>
        </div>
      )}

      {userProfiles.length === 0 && !showCreate && (
        <p className="text-sm text-muted-foreground text-center py-4 border border-dashed border-border rounded-xl">
          Noch keine eigenen Profile erstellt
        </p>
      )}
    </div>
  );
}

function ProfileCard({
  profile,
  readonly,
  onEdit,
  onDelete,
}: {
  profile: ShareProfile;
  readonly?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const permCount = Object.values(profile.permissions).filter(Boolean).length;
  const permNames = (Object.keys(profile.permissions) as Array<keyof SharePermissions>)
    .filter((k) => profile.permissions[k])
    .map((k) => PERMISSION_LABELS[k])
    .join(', ');

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <span className="text-2xl">{profile.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium">{profile.name}</p>
            {readonly && (
              <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                System
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {permCount} Bereiche: {permNames}
          </p>
        </div>
        {!readonly && (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </Card>
  );
}

function ProfileForm({
  initialData,
  onSave,
  onCancel,
}: {
  initialData?: ShareProfile;
  onSave: (data: { name: string; icon: string; permissions: SharePermissions }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialData?.name || '');
  const [icon, setIcon] = useState(initialData?.icon || '📋');
  const [permissions, setPermissions] = useState<SharePermissions>(
    initialData?.permissions || DEFAULT_PERMISSIONS
  );
  const [isSaving, setIsSaving] = useState(false);

  const togglePermission = (key: keyof SharePermissions) => {
    setPermissions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Bitte gib einen Namen ein');
      return;
    }
    setIsSaving(true);
    try {
      await onSave({ name: name.trim(), icon, permissions });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="p-4 border-foreground/20">
      <div className="space-y-4">
        <div className="flex gap-3">
          <div className="space-y-1.5 flex-1">
            <Label>Name</Label>
            <Input
              placeholder="z.B. Immobilienmakler"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Symbol</Label>
          <div className="flex flex-wrap gap-1.5">
            {ICONS.map((i) => (
              <button
                key={i}
                onClick={() => setIcon(i)}
                className={`w-9 h-9 rounded-lg text-lg flex items-center justify-center transition-colors ${
                  icon === i
                    ? 'bg-foreground/10 ring-2 ring-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {i}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Berechtigungen</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(Object.keys(PERMISSION_LABELS) as Array<keyof SharePermissions>).map((key) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissions[key]}
                  onChange={() => togglePermission(key)}
                  className="w-4 h-4 rounded accent-foreground"
                />
                <span className="text-sm">{PERMISSION_LABELS[key]}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" />
            Abbrechen
          </Button>
          <Button size="sm" onClick={handleSave} isLoading={isSaving}>
            <Check className="w-4 h-4 mr-1" />
            Speichern
          </Button>
        </div>
      </div>
    </Card>
  );
}
